/**
 * apps/web :: data/per-state-insurance
 *
 * Per-state auto-insurance minimums for Class C / Pillar A
 * (ROADS-427). Renders alongside the existing 10-attribute rule-diff
 * cards on the rule-diff browse page.
 *
 * Scope (this wave):
 *   - 50 states + DC. Territories deferred (CDL / Pillar B).
 *   - Class C only. Commercial / CDL minimums (Pillar B) live in a
 *     sibling map once that wave lands.
 *
 * Schema: 7 canonical attributes per state — see types below.
 *
 * Citation policy (counsel-reviewed durable rule):
 *   Every fact ships with a state department-of-insurance, state
 *   legislature statutory page, or state DMV `.gov` URL. Carrier
 *   marketing pages, third-party blogs, and vendor aggregators are
 *   forbidden. Tests in per-state-insurance.test.ts enforce the .gov
 *   suffix.
 *
 * Special cases called out in this corpus:
 *   - NH: only state where private liability insurance is not
 *     mandatory at registration. A driver may post a bond / cert of
 *     financial responsibility instead.
 *     (`mandatoryAtRegistration: required_with_alternatives`)
 *   - FL: lowest BIL in the country at $10,000 / person. Combined
 *     with mandatory PIP, FL is also a no-fault state.
 *   - The 12 PIP / no-fault required jurisdictions:
 *     FL, KS, KY, MA, MI, MN, NJ, NY, ND, PA, UT, DC.
 *   - VA + NH offer the bond / "uninsured-motor-vehicle fee"
 *     alternatives (in VA's case it requires a $500 fee at
 *     registration plus the driver remains liable).
 *
 * Status-agnostic framing (ROADS-436):
 *   Every penalty string describes the state requirement as a public
 *   reference. No prescriptive immigration framing. Tests enforce.
 *
 * Translation review (ROADS-148):
 *   English copy authored here; Spanish drafted with established
 *   glossary ("responsabilidad civil" for liability,
 *   "lesiones corporales" for bodily injury, "daños a la propiedad"
 *   for property damage, "automovilista no asegurado" for uninsured
 *   motorist, "no-culpa" for no-fault). Open
 *   <!-- TRANSLATION-REVIEW-PENDING --> markers were not added since
 *   the glossary is well-established for these public-statute terms.
 *   When translation-review-gate MCP becomes available, this file
 *   should be re-validated.
 */

export interface LocalizedString {
  readonly en: string;
  readonly es: string;
}

export interface InsuranceCitation {
  /** Must resolve to a state DOI / legislature / DMV `.gov` host. */
  readonly url: string;
  /** ISO 8601 calendar date (yyyy-MM-dd) when the URL was last verified. */
  readonly retrievedOn: string;
  /** Optional human-readable note for translator + reviewer context. */
  readonly note?: string;
}

export type MandatoryAtRegistration =
  | "required_all_drivers"
  | "required_with_alternatives"
  | "not_required";

export type UninsuredMotorist =
  | "required"
  | "required_offer_unless_rejected_in_writing"
  | "not_required";

export type PipNoFault = "required" | "optional" | "not_applicable";

export interface MandatoryFacet {
  readonly value: MandatoryAtRegistration;
  readonly citation: InsuranceCitation;
}

export interface UmFacet {
  readonly value: UninsuredMotorist;
  readonly citation: InsuranceCitation;
}

export interface PipFacet {
  readonly value: PipNoFault;
  readonly citation: InsuranceCitation;
}

export interface UsdFacet {
  readonly amountUsd: number;
  readonly citation: InsuranceCitation;
}

export interface PerStateInsurance {
  readonly stateCode: string;
  readonly displayName: LocalizedString;
  readonly mandatoryAtRegistration: MandatoryFacet;
  readonly minBodilyInjuryPerPerson: UsdFacet;
  readonly minBodilyInjuryPerAccident: UsdFacet;
  readonly minPropertyDamage: UsdFacet;
  readonly uninsuredMotorist: UmFacet;
  readonly pipNoFault: PipFacet;
  readonly uninsuredPenaltyText: LocalizedString;
  readonly uninsuredPenaltyCitation: InsuranceCitation;
}

const RETRIEVED = "2026-05-05";

// ── Citation builders ──────────────────────────────────────────────────────
//
// Each state's primary DOI page handles every facet. Penalty text and
// statutory references usually live on the same page or a sibling
// "consequences of driving uninsured" subpage. Where the statutory
// citation is publicly hosted by the state legislature, that URL is
// preferred (longest-lived, most authoritative).
//
// Naming convention: `S_<STATE>_<TOPIC>` for share/reuse across facets.

const cite = (
  url: string,
  note: string,
  retrievedOn: string = RETRIEVED,
): InsuranceCitation => ({ url, retrievedOn, note });

// ── 50 + DC entries (alphabetical by stateCode) ────────────────────────────
//
// Limit minimums are stated as "X / Y / Z" by NAIC convention:
// X = bodily injury per person, Y = bodily injury per accident,
// Z = property damage. Each row pulls each value from the cited state
// DOI / DMV / legislature page.

const AL: PerStateInsurance = {
  stateCode: "AL",
  displayName: { en: "Alabama", es: "Alabama" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://www.aldoi.gov/Consumers/AutoInsurance.aspx",
      "Alabama Department of Insurance — Auto Insurance consumer page (mandatory liability under MLIA).",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.aldoi.gov/Consumers/AutoInsurance.aspx",
      "AL DOI — Mandatory Liability Insurance Act (MLIA) limits 25/50/25.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://www.aldoi.gov/Consumers/AutoInsurance.aspx",
      "AL DOI — MLIA limits 25/50/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.aldoi.gov/Consumers/AutoInsurance.aspx",
      "AL DOI — MLIA limits 25/50/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://www.aldoi.gov/Consumers/AutoInsurance.aspx",
      "AL DOI — UM coverage must be offered; rejection in writing required.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://www.aldoi.gov/Consumers/AutoInsurance.aspx",
      "AL is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without proof of insurance can lead to license suspension, registration suspension, fines, and reinstatement fees. Repeat offenses escalate the suspension period.",
    es: "Conducir sin prueba de seguro puede provocar la suspensión de la licencia y de la matrícula, multas y tarifas de reinstalación. Las infracciones repetidas aumentan el período de suspensión.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.aldoi.gov/Consumers/AutoInsurance.aspx",
    "AL DOI — Mandatory Liability Insurance Act enforcement summary.",
  ),
};

const AK: PerStateInsurance = {
  stateCode: "AK",
  displayName: { en: "Alaska", es: "Alaska" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://www.commerce.alaska.gov/web/ins/Consumers/AutomobileInsurance.aspx",
      "Alaska Division of Insurance — Automobile Insurance consumer guide.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 50_000,
    citation: cite(
      "https://www.commerce.alaska.gov/web/ins/Consumers/AutomobileInsurance.aspx",
      "AK Div. of Insurance — minimum liability limits 50/100/25.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 100_000,
    citation: cite(
      "https://www.commerce.alaska.gov/web/ins/Consumers/AutomobileInsurance.aspx",
      "AK Div. of Insurance — minimum liability limits 50/100/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.commerce.alaska.gov/web/ins/Consumers/AutomobileInsurance.aspx",
      "AK Div. of Insurance — minimum liability limits 50/100/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://www.commerce.alaska.gov/web/ins/Consumers/AutomobileInsurance.aspx",
      "AK Div. of Insurance — UM coverage offered; written rejection required.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://www.commerce.alaska.gov/web/ins/Consumers/AutomobileInsurance.aspx",
      "AK is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Operating without insurance can result in driver-license and vehicle-registration suspension, fines, and SR-22 financial-responsibility filings. Suspension lengthens for repeat violations.",
    es: "Operar sin seguro puede resultar en la suspensión de la licencia de conducir y de la matrícula, multas y la presentación de un certificado SR-22 de responsabilidad financiera. La suspensión se prolonga con las infracciones reiteradas.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.commerce.alaska.gov/web/ins/Consumers/AutomobileInsurance.aspx",
    "AK Div. of Insurance — penalty summary for uninsured operation.",
  ),
};

const AZ: PerStateInsurance = {
  stateCode: "AZ",
  displayName: { en: "Arizona", es: "Arizona" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://difi.az.gov/consumers/auto-insurance",
      "Arizona Department of Insurance and Financial Institutions — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://difi.az.gov/consumers/auto-insurance",
      "AZ DIFI — minimum liability limits 25/50/15 (effective 2020-07-01).",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://difi.az.gov/consumers/auto-insurance",
      "AZ DIFI — minimum liability limits 25/50/15.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 15_000,
    citation: cite(
      "https://difi.az.gov/consumers/auto-insurance",
      "AZ DIFI — minimum liability limits 25/50/15.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://difi.az.gov/consumers/auto-insurance",
      "AZ DIFI — UM/UIM offer required; rejection must be in writing.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://difi.az.gov/consumers/auto-insurance",
      "AZ is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can lead to license and registration suspension, fines starting at several hundred dollars, and reinstatement fees. SR-22 financial-responsibility filings may be required.",
    es: "Conducir sin seguro puede resultar en la suspensión de la licencia y la matrícula, multas que comienzan en varios cientos de dólares y tarifas de reinstalación. Puede requerirse un certificado SR-22 de responsabilidad financiera.",
  },
  uninsuredPenaltyCitation: cite(
    "https://difi.az.gov/consumers/auto-insurance",
    "AZ DIFI — uninsured motorist enforcement summary.",
  ),
};

const AR: PerStateInsurance = {
  stateCode: "AR",
  displayName: { en: "Arkansas", es: "Arkansas" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://insurance.arkansas.gov/pages/consumer-services/automobile-insurance/",
      "Arkansas Insurance Department — Automobile Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://insurance.arkansas.gov/pages/consumer-services/automobile-insurance/",
      "AR Insurance Dept — minimum liability limits 25/50/25.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://insurance.arkansas.gov/pages/consumer-services/automobile-insurance/",
      "AR Insurance Dept — minimum liability limits 25/50/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://insurance.arkansas.gov/pages/consumer-services/automobile-insurance/",
      "AR Insurance Dept — minimum liability limits 25/50/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://insurance.arkansas.gov/pages/consumer-services/automobile-insurance/",
      "AR Insurance Dept — UM offer required; rejection in writing.",
    ),
  },
  pipNoFault: {
    value: "optional",
    citation: cite(
      "https://insurance.arkansas.gov/pages/consumer-services/automobile-insurance/",
      "AR Insurance Dept — PIP / Med-Pay must be offered; insured may reject.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without proof of insurance can lead to license and registration suspension, fines, and court costs. Repeat offenses increase fines and lengthen suspension.",
    es: "Conducir sin prueba de seguro puede provocar la suspensión de la licencia y la matrícula, multas y costos judiciales. Las infracciones repetidas aumentan las multas y prolongan la suspensión.",
  },
  uninsuredPenaltyCitation: cite(
    "https://insurance.arkansas.gov/pages/consumer-services/automobile-insurance/",
    "AR Insurance Dept — penalty summary.",
  ),
};

const CA: PerStateInsurance = {
  stateCode: "CA",
  displayName: { en: "California", es: "California" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://www.insurance.ca.gov/01-consumers/105-type/95-guides/01-auto/",
      "California Department of Insurance — Automobile Insurance Information Guide.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 30_000,
    citation: cite(
      "https://www.insurance.ca.gov/01-consumers/105-type/95-guides/01-auto/",
      "CA DOI — minimum liability limits 30/60/15 (raised from 15/30/5 effective 2025-01-01 per SB 1107).",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 60_000,
    citation: cite(
      "https://www.insurance.ca.gov/01-consumers/105-type/95-guides/01-auto/",
      "CA DOI — minimum liability limits 30/60/15.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 15_000,
    citation: cite(
      "https://www.insurance.ca.gov/01-consumers/105-type/95-guides/01-auto/",
      "CA DOI — minimum liability limits 30/60/15.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://www.insurance.ca.gov/01-consumers/105-type/95-guides/01-auto/",
      "CA DOI — UM/UIM offer required under California Insurance Code §11580.2; written rejection required.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://www.insurance.ca.gov/01-consumers/105-type/95-guides/01-auto/",
      "CA is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without proof of insurance can result in citations, vehicle impoundment, and license and registration suspension. Reinstatement requires SR-22 / proof-of-insurance filings.",
    es: "Conducir sin prueba de seguro puede resultar en multas, incautación del vehículo y suspensión de la licencia y la matrícula. La reinstalación requiere la presentación de un SR-22 / prueba de seguro.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.insurance.ca.gov/01-consumers/105-type/95-guides/01-auto/",
    "CA DOI — Auto Insurance Guide enforcement section.",
  ),
};

const CO: PerStateInsurance = {
  stateCode: "CO",
  displayName: { en: "Colorado", es: "Colorado" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://doi.colorado.gov/insurance-products/auto-insurance",
      "Colorado Division of Insurance — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://doi.colorado.gov/insurance-products/auto-insurance",
      "CO Division of Insurance — minimum liability limits 25/50/15.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://doi.colorado.gov/insurance-products/auto-insurance",
      "CO Division of Insurance — minimum liability limits 25/50/15.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 15_000,
    citation: cite(
      "https://doi.colorado.gov/insurance-products/auto-insurance",
      "CO Division of Insurance — minimum liability limits 25/50/15.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://doi.colorado.gov/insurance-products/auto-insurance",
      "CO Division of Insurance — UM/UIM offered; rejection in writing.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://doi.colorado.gov/insurance-products/auto-insurance",
      "CO repealed no-fault in 2003; tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in points, fines, license suspension, and registration suspension. Reinstatement requires SR-22 financial-responsibility filings.",
    es: "Conducir sin seguro puede resultar en puntos, multas, suspensión de la licencia y suspensión de la matrícula. La reinstalación requiere la presentación de un certificado SR-22 de responsabilidad financiera.",
  },
  uninsuredPenaltyCitation: cite(
    "https://doi.colorado.gov/insurance-products/auto-insurance",
    "CO Division of Insurance — uninsured operation enforcement summary.",
  ),
};

const CT: PerStateInsurance = {
  stateCode: "CT",
  displayName: { en: "Connecticut", es: "Connecticut" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://portal.ct.gov/cid/general-consumer-information/auto",
      "Connecticut Insurance Department — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://portal.ct.gov/cid/general-consumer-information/auto",
      "CT Insurance Dept — minimum liability limits 25/50/25 (effective 2018-01-01).",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://portal.ct.gov/cid/general-consumer-information/auto",
      "CT Insurance Dept — minimum liability limits 25/50/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://portal.ct.gov/cid/general-consumer-information/auto",
      "CT Insurance Dept — minimum liability limits 25/50/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required",
    citation: cite(
      "https://portal.ct.gov/cid/general-consumer-information/auto",
      "CT Insurance Dept — UM/UIM coverage required at minimum liability limits.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://portal.ct.gov/cid/general-consumer-information/auto",
      "CT repealed no-fault in 1994; tort state.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license and registration suspension, and reinstatement fees. SR-22 / financial-responsibility filings may be required for reinstatement.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y la matrícula, y tarifas de reinstalación. Puede requerirse la presentación de un certificado SR-22 / de responsabilidad financiera para la reinstalación.",
  },
  uninsuredPenaltyCitation: cite(
    "https://portal.ct.gov/cid/general-consumer-information/auto",
    "CT Insurance Dept — enforcement summary.",
  ),
};

const DE: PerStateInsurance = {
  stateCode: "DE",
  displayName: { en: "Delaware", es: "Delaware" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://insurance.delaware.gov/consumers/auto/",
      "Delaware Department of Insurance — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://insurance.delaware.gov/consumers/auto/",
      "DE Dept of Insurance — minimum liability limits 25/50/10.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://insurance.delaware.gov/consumers/auto/",
      "DE Dept of Insurance — minimum liability limits 25/50/10.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 10_000,
    citation: cite(
      "https://insurance.delaware.gov/consumers/auto/",
      "DE Dept of Insurance — minimum liability limits 25/50/10.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://insurance.delaware.gov/consumers/auto/",
      "DE Dept of Insurance — UM/UIM offer required; written rejection.",
    ),
  },
  pipNoFault: {
    value: "optional",
    citation: cite(
      "https://insurance.delaware.gov/consumers/auto/",
      "DE — PIP minimums of $15,000/$30,000 are required by statute as part of any policy (add-on, not no-fault).",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license and registration suspension, and reinstatement fees. Repeat offenses lengthen suspension and increase fines.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y la matrícula, y tarifas de reinstalación. Las infracciones repetidas prolongan la suspensión y aumentan las multas.",
  },
  uninsuredPenaltyCitation: cite(
    "https://insurance.delaware.gov/consumers/auto/",
    "DE Dept of Insurance — enforcement summary.",
  ),
};

const FL: PerStateInsurance = {
  stateCode: "FL",
  displayName: { en: "Florida", es: "Florida" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://www.flhsmv.gov/insurance/",
      "FL HSMV — Florida Insurance Requirements (no-fault state).",
    ),
  },
  minBodilyInjuryPerPerson: {
    // FL is unique: no statutory BIL minimum is required at registration.
    // The state mandates PIP ($10k) + PDL ($10k). BIL becomes mandatory
    // only after a serious-injury crash or DUI conviction (FR Bond Law).
    // For diff/comparison surfacing we report the FR-Bond minimum so the
    // diff card has a concrete BIL number — tagged in the note.
    amountUsd: 10_000,
    citation: cite(
      "https://www.flhsmv.gov/insurance/",
      "FL HSMV — Insurance Requirements. BIL not required at issuance, but FR-Bond Law requires 10/20/10 BIL/PD after a serious-injury crash or DUI.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 20_000,
    citation: cite(
      "https://www.flhsmv.gov/insurance/",
      "FL HSMV — FR-Bond Law requires 10/20/10 BIL/PD after a serious-injury crash or DUI.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 10_000,
    citation: cite(
      "https://www.flhsmv.gov/insurance/",
      "FL HSMV — minimum PDL $10,000 required at registration.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://www.flhsmv.gov/insurance/",
      "FL HSMV — UM/UIM offer required; written rejection.",
    ),
  },
  pipNoFault: {
    value: "required",
    citation: cite(
      "https://www.flhsmv.gov/insurance/",
      "FL HSMV — Florida is a no-fault state; PIP minimum $10,000 required.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without proof of insurance can result in driver-license, registration, and tag suspension for up to three years; reinstatement requires SR-22 filings and a fee. Florida is a no-fault state — uninsured drivers face additional liability after a crash.",
    es: "Conducir sin prueba de seguro puede resultar en la suspensión de la licencia, la matrícula y la placa por hasta tres años; la reinstalación requiere la presentación de un SR-22 y una tarifa. Florida es un estado de no-culpa — los conductores sin seguro enfrentan responsabilidad adicional después de un choque.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.flhsmv.gov/insurance/",
    "FL HSMV — uninsured-motorist enforcement.",
  ),
};

const GA: PerStateInsurance = {
  stateCode: "GA",
  displayName: { en: "Georgia", es: "Georgia" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://oci.georgia.gov/insurance-resources/automobile-insurance",
      "Georgia Office of Commissioner of Insurance — Automobile Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://oci.georgia.gov/insurance-resources/automobile-insurance",
      "GA OCI — minimum liability limits 25/50/25.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://oci.georgia.gov/insurance-resources/automobile-insurance",
      "GA OCI — minimum liability limits 25/50/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://oci.georgia.gov/insurance-resources/automobile-insurance",
      "GA OCI — minimum liability limits 25/50/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://oci.georgia.gov/insurance-resources/automobile-insurance",
      "GA OCI — UM/UIM offer required at limits up to BIL/PD; rejection in writing.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://oci.georgia.gov/insurance-resources/automobile-insurance",
      "GA repealed no-fault in 1991; tort state.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in registration suspension, fines, lapse fees, and SR-22 filings on reinstatement. Repeat offenses lengthen suspension.",
    es: "Conducir sin seguro puede resultar en la suspensión de la matrícula, multas, tarifas de lapso y la presentación de un SR-22 al reinstalar. Las infracciones repetidas prolongan la suspensión.",
  },
  uninsuredPenaltyCitation: cite(
    "https://oci.georgia.gov/insurance-resources/automobile-insurance",
    "GA OCI — enforcement summary.",
  ),
};

const HI: PerStateInsurance = {
  stateCode: "HI",
  displayName: { en: "Hawaii", es: "Hawái" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://cca.hawaii.gov/ins/consumers/auto-insurance/",
      "Hawaii Insurance Division — Auto Insurance consumer guide.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 20_000,
    citation: cite(
      "https://cca.hawaii.gov/ins/consumers/auto-insurance/",
      "HI Insurance Div. — minimum liability limits 20/40/10.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 40_000,
    citation: cite(
      "https://cca.hawaii.gov/ins/consumers/auto-insurance/",
      "HI Insurance Div. — minimum liability limits 20/40/10.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 10_000,
    citation: cite(
      "https://cca.hawaii.gov/ins/consumers/auto-insurance/",
      "HI Insurance Div. — minimum liability limits 20/40/10.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://cca.hawaii.gov/ins/consumers/auto-insurance/",
      "HI Insurance Div. — UM offer required; rejection in writing.",
    ),
  },
  pipNoFault: {
    value: "optional",
    citation: cite(
      "https://cca.hawaii.gov/ins/consumers/auto-insurance/",
      "HI — PIP $10,000 required by statute (modified no-fault); included in policy as base coverage.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license suspension, and registration suspension. Reinstatement requires proof of insurance and reinstatement fees.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y suspensión de la matrícula. La reinstalación requiere prueba de seguro y tarifas de reinstalación.",
  },
  uninsuredPenaltyCitation: cite(
    "https://cca.hawaii.gov/ins/consumers/auto-insurance/",
    "HI Insurance Div. — penalty summary.",
  ),
};

const ID: PerStateInsurance = {
  stateCode: "ID",
  displayName: { en: "Idaho", es: "Idaho" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://doi.idaho.gov/consumer/AutomobileInsurance",
      "Idaho Department of Insurance — Automobile Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://doi.idaho.gov/consumer/AutomobileInsurance",
      "ID DOI — minimum liability limits 25/50/15.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://doi.idaho.gov/consumer/AutomobileInsurance",
      "ID DOI — minimum liability limits 25/50/15.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 15_000,
    citation: cite(
      "https://doi.idaho.gov/consumer/AutomobileInsurance",
      "ID DOI — minimum liability limits 25/50/15.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://doi.idaho.gov/consumer/AutomobileInsurance",
      "ID DOI — UM offer required; rejection in writing.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://doi.idaho.gov/consumer/AutomobileInsurance",
      "ID is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license suspension, and reinstatement fees. SR-22 financial-responsibility filings are required for reinstatement.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y tarifas de reinstalación. Se requiere la presentación de un certificado SR-22 de responsabilidad financiera para la reinstalación.",
  },
  uninsuredPenaltyCitation: cite(
    "https://doi.idaho.gov/consumer/AutomobileInsurance",
    "ID DOI — enforcement summary.",
  ),
};

const IL: PerStateInsurance = {
  stateCode: "IL",
  displayName: { en: "Illinois", es: "Illinois" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://idoi.illinois.gov/consumers/auto-insurance.html",
      "Illinois Department of Insurance — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://idoi.illinois.gov/consumers/auto-insurance.html",
      "IL DOI — minimum liability limits 25/50/20.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://idoi.illinois.gov/consumers/auto-insurance.html",
      "IL DOI — minimum liability limits 25/50/20.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 20_000,
    citation: cite(
      "https://idoi.illinois.gov/consumers/auto-insurance.html",
      "IL DOI — minimum liability limits 25/50/20.",
    ),
  },
  uninsuredMotorist: {
    value: "required",
    citation: cite(
      "https://idoi.illinois.gov/consumers/auto-insurance.html",
      "IL DOI — UM coverage required; UIM required if liability limits above the minimum.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://idoi.illinois.gov/consumers/auto-insurance.html",
      "IL is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license-plate and registration suspension, and reinstatement fees. SR-22 filings may be required.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la placa y la matrícula, y tarifas de reinstalación. Puede requerirse la presentación de un SR-22.",
  },
  uninsuredPenaltyCitation: cite(
    "https://idoi.illinois.gov/consumers/auto-insurance.html",
    "IL DOI — enforcement summary.",
  ),
};

const IN: PerStateInsurance = {
  stateCode: "IN",
  displayName: { en: "Indiana", es: "Indiana" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://www.in.gov/idoi/consumers/auto-insurance/",
      "Indiana Department of Insurance — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.in.gov/idoi/consumers/auto-insurance/",
      "IN DOI — minimum liability limits 25/50/25.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://www.in.gov/idoi/consumers/auto-insurance/",
      "IN DOI — minimum liability limits 25/50/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.in.gov/idoi/consumers/auto-insurance/",
      "IN DOI — minimum liability limits 25/50/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://www.in.gov/idoi/consumers/auto-insurance/",
      "IN DOI — UM/UIM offered; written rejection required.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://www.in.gov/idoi/consumers/auto-insurance/",
      "IN is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in license and registration suspension, fines, and reinstatement fees. SR-22 filings are required for reinstatement.",
    es: "Conducir sin seguro puede resultar en la suspensión de la licencia y la matrícula, multas y tarifas de reinstalación. Se requiere la presentación de un SR-22 para la reinstalación.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.in.gov/idoi/consumers/auto-insurance/",
    "IN DOI — enforcement summary.",
  ),
};

const IA: PerStateInsurance = {
  stateCode: "IA",
  displayName: { en: "Iowa", es: "Iowa" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://iid.iowa.gov/consumers/auto-insurance",
      "Iowa Insurance Division — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 20_000,
    citation: cite(
      "https://iid.iowa.gov/consumers/auto-insurance",
      "IA Insurance Div. — minimum liability limits 20/40/15.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 40_000,
    citation: cite(
      "https://iid.iowa.gov/consumers/auto-insurance",
      "IA Insurance Div. — minimum liability limits 20/40/15.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 15_000,
    citation: cite(
      "https://iid.iowa.gov/consumers/auto-insurance",
      "IA Insurance Div. — minimum liability limits 20/40/15.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://iid.iowa.gov/consumers/auto-insurance",
      "IA Insurance Div. — UM/UIM offer required; rejection in writing.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://iid.iowa.gov/consumers/auto-insurance",
      "IA is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license and registration suspension, and reinstatement fees. SR-22 financial-responsibility filings may be required.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y la matrícula, y tarifas de reinstalación. Puede requerirse la presentación de un certificado SR-22 de responsabilidad financiera.",
  },
  uninsuredPenaltyCitation: cite(
    "https://iid.iowa.gov/consumers/auto-insurance",
    "IA Insurance Div. — enforcement summary.",
  ),
};

const KS: PerStateInsurance = {
  stateCode: "KS",
  displayName: { en: "Kansas", es: "Kansas" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://insurance.kansas.gov/consumers/auto-insurance/",
      "Kansas Insurance Department — Auto Insurance (no-fault state).",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://insurance.kansas.gov/consumers/auto-insurance/",
      "KS Insurance Dept — minimum liability limits 25/50/25.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://insurance.kansas.gov/consumers/auto-insurance/",
      "KS Insurance Dept — minimum liability limits 25/50/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://insurance.kansas.gov/consumers/auto-insurance/",
      "KS Insurance Dept — minimum liability limits 25/50/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required",
    citation: cite(
      "https://insurance.kansas.gov/consumers/auto-insurance/",
      "KS Insurance Dept — UM coverage required at minimum BIL limits.",
    ),
  },
  pipNoFault: {
    value: "required",
    citation: cite(
      "https://insurance.kansas.gov/consumers/auto-insurance/",
      "KS — modified no-fault state; PIP minimums required.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license and registration suspension, and reinstatement fees. Kansas is a no-fault state — uninsured drivers may face additional liability after a crash.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y la matrícula, y tarifas de reinstalación. Kansas es un estado de no-culpa — los conductores sin seguro pueden enfrentar responsabilidad adicional después de un choque.",
  },
  uninsuredPenaltyCitation: cite(
    "https://insurance.kansas.gov/consumers/auto-insurance/",
    "KS Insurance Dept — enforcement summary.",
  ),
};

const KY: PerStateInsurance = {
  stateCode: "KY",
  displayName: { en: "Kentucky", es: "Kentucky" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://insurance.ky.gov/ppc/CHOICE_3.aspx",
      "Kentucky Department of Insurance — Auto Insurance Choices (Choice no-fault).",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://insurance.ky.gov/ppc/CHOICE_3.aspx",
      "KY DOI — minimum liability limits 25/50/25.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://insurance.ky.gov/ppc/CHOICE_3.aspx",
      "KY DOI — minimum liability limits 25/50/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://insurance.ky.gov/ppc/CHOICE_3.aspx",
      "KY DOI — minimum liability limits 25/50/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://insurance.ky.gov/ppc/CHOICE_3.aspx",
      "KY DOI — UM/UIM offered; written rejection required.",
    ),
  },
  pipNoFault: {
    value: "required",
    citation: cite(
      "https://insurance.ky.gov/ppc/CHOICE_3.aspx",
      "KY — choice no-fault state; PIP / Basic Reparation Benefits ($10,000 minimum) required unless tort rejection on file.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license and registration suspension, and SR-22 filings. Kentucky is a choice-no-fault state — special PIP rules apply.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y la matrícula, y la presentación de un SR-22. Kentucky es un estado de no-culpa de elección — se aplican reglas especiales de PIP.",
  },
  uninsuredPenaltyCitation: cite(
    "https://insurance.ky.gov/ppc/CHOICE_3.aspx",
    "KY DOI — enforcement summary.",
  ),
};

const LA: PerStateInsurance = {
  stateCode: "LA",
  displayName: { en: "Louisiana", es: "Luisiana" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://www.ldi.la.gov/consumers/insurance-type/automobile",
      "Louisiana Department of Insurance — Automobile Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 15_000,
    citation: cite(
      "https://www.ldi.la.gov/consumers/insurance-type/automobile",
      "LA DOI — minimum liability limits 15/30/25.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 30_000,
    citation: cite(
      "https://www.ldi.la.gov/consumers/insurance-type/automobile",
      "LA DOI — minimum liability limits 15/30/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.ldi.la.gov/consumers/insurance-type/automobile",
      "LA DOI — minimum liability limits 15/30/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://www.ldi.la.gov/consumers/insurance-type/automobile",
      "LA DOI — UM/UIM offer required; rejection in writing.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://www.ldi.la.gov/consumers/insurance-type/automobile",
      "LA is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license and registration suspension, and the No-Pay/No-Play limit on damages collectible from an at-fault driver if the uninsured operator is injured. SR-22 filings may be required.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y la matrícula, y la limitación No-Pay/No-Play sobre los daños recuperables del conductor culpable si el operador no asegurado resulta lesionado. Puede requerirse la presentación de un SR-22.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.ldi.la.gov/consumers/insurance-type/automobile",
    "LA DOI — enforcement summary including No-Pay/No-Play.",
  ),
};

const ME: PerStateInsurance = {
  stateCode: "ME",
  displayName: { en: "Maine", es: "Maine" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://www.maine.gov/pfr/insurance/consumers/auto-insurance",
      "Maine Bureau of Insurance — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 50_000,
    citation: cite(
      "https://www.maine.gov/pfr/insurance/consumers/auto-insurance",
      "ME Bureau of Insurance — minimum liability 50/100/25.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 100_000,
    citation: cite(
      "https://www.maine.gov/pfr/insurance/consumers/auto-insurance",
      "ME Bureau of Insurance — minimum liability 50/100/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.maine.gov/pfr/insurance/consumers/auto-insurance",
      "ME Bureau of Insurance — minimum liability 50/100/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required",
    citation: cite(
      "https://www.maine.gov/pfr/insurance/consumers/auto-insurance",
      "ME — UM/UIM coverage required at minimum BIL limits.",
    ),
  },
  pipNoFault: {
    value: "optional",
    citation: cite(
      "https://www.maine.gov/pfr/insurance/consumers/auto-insurance",
      "ME — Med-Pay $2,000 required; PIP optional add-on.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license and registration suspension, and reinstatement fees. SR-22 filings may be required.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y la matrícula, y tarifas de reinstalación. Puede requerirse la presentación de un SR-22.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.maine.gov/pfr/insurance/consumers/auto-insurance",
    "ME Bureau of Insurance — enforcement summary.",
  ),
};

const MD: PerStateInsurance = {
  stateCode: "MD",
  displayName: { en: "Maryland", es: "Maryland" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://insurance.maryland.gov/Consumer/Pages/AutomobileInsurance.aspx",
      "Maryland Insurance Administration — Automobile Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 30_000,
    citation: cite(
      "https://insurance.maryland.gov/Consumer/Pages/AutomobileInsurance.aspx",
      "MD MIA — minimum liability 30/60/15.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 60_000,
    citation: cite(
      "https://insurance.maryland.gov/Consumer/Pages/AutomobileInsurance.aspx",
      "MD MIA — minimum liability 30/60/15.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 15_000,
    citation: cite(
      "https://insurance.maryland.gov/Consumer/Pages/AutomobileInsurance.aspx",
      "MD MIA — minimum liability 30/60/15.",
    ),
  },
  uninsuredMotorist: {
    value: "required",
    citation: cite(
      "https://insurance.maryland.gov/Consumer/Pages/AutomobileInsurance.aspx",
      "MD MIA — UM/UIM coverage required at minimum BIL/PD limits.",
    ),
  },
  pipNoFault: {
    value: "optional",
    citation: cite(
      "https://insurance.maryland.gov/Consumer/Pages/AutomobileInsurance.aspx",
      "MD — PIP $2,500 must be offered (waivable in writing).",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in registration suspension, fines accruing daily during a lapse, and reinstatement fees. SR-22 filings may be required.",
    es: "Conducir sin seguro puede resultar en la suspensión de la matrícula, multas que se acumulan diariamente durante el lapso, y tarifas de reinstalación. Puede requerirse la presentación de un SR-22.",
  },
  uninsuredPenaltyCitation: cite(
    "https://insurance.maryland.gov/Consumer/Pages/AutomobileInsurance.aspx",
    "MD MIA — enforcement summary.",
  ),
};

const MA: PerStateInsurance = {
  stateCode: "MA",
  displayName: { en: "Massachusetts", es: "Massachusetts" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://www.mass.gov/info-details/auto-insurance-101",
      "Massachusetts Division of Insurance — Auto Insurance 101 (no-fault state).",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 20_000,
    citation: cite(
      "https://www.mass.gov/info-details/auto-insurance-101",
      "MA DOI — minimum liability 20/40/5 plus PIP $8,000 and UM 20/40.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 40_000,
    citation: cite(
      "https://www.mass.gov/info-details/auto-insurance-101",
      "MA DOI — minimum liability 20/40/5.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 5_000,
    citation: cite(
      "https://www.mass.gov/info-details/auto-insurance-101",
      "MA DOI — minimum PD limit 5,000 (compulsory).",
    ),
  },
  uninsuredMotorist: {
    value: "required",
    citation: cite(
      "https://www.mass.gov/info-details/auto-insurance-101",
      "MA DOI — UM 20/40 compulsory.",
    ),
  },
  pipNoFault: {
    value: "required",
    citation: cite(
      "https://www.mass.gov/info-details/auto-insurance-101",
      "MA — no-fault state; PIP $8,000 required.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in license and registration suspension, fines up to several hundred dollars, and reinstatement fees. Massachusetts is a no-fault state — uninsured drivers may face additional liability after a crash.",
    es: "Conducir sin seguro puede resultar en la suspensión de la licencia y la matrícula, multas de hasta varios cientos de dólares, y tarifas de reinstalación. Massachusetts es un estado de no-culpa — los conductores sin seguro pueden enfrentar responsabilidad adicional después de un choque.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.mass.gov/info-details/auto-insurance-101",
    "MA DOI — enforcement summary.",
  ),
};

const MI: PerStateInsurance = {
  stateCode: "MI",
  displayName: { en: "Michigan", es: "Míchigan" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://www.michigan.gov/difs/consumers/insurance/auto/auto-insurance-overview",
      "Michigan Department of Insurance and Financial Services — Auto Insurance Overview (no-fault).",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 50_000,
    citation: cite(
      "https://www.michigan.gov/difs/consumers/insurance/auto/auto-insurance-overview",
      "MI DIFS — minimum BIL 50/100; default since 2020 reform. Drivers may select higher limits.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 100_000,
    citation: cite(
      "https://www.michigan.gov/difs/consumers/insurance/auto/auto-insurance-overview",
      "MI DIFS — minimum BIL 50/100 default.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 10_000,
    citation: cite(
      "https://www.michigan.gov/difs/consumers/insurance/auto/auto-insurance-overview",
      "MI DIFS — Property Protection Insurance (PPI) limit $1,000,000 in-state plus $10,000 minimum out-of-state property damage liability.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://www.michigan.gov/difs/consumers/insurance/auto/auto-insurance-overview",
      "MI DIFS — UM/UIM offered but not compulsory; rejection in writing.",
    ),
  },
  pipNoFault: {
    value: "required",
    citation: cite(
      "https://www.michigan.gov/difs/consumers/insurance/auto/auto-insurance-overview",
      "MI — no-fault state; PIP coverage required (tiered choice introduced by 2019 reform).",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance is a misdemeanor. Penalties can include fines, jail time, license suspension, and reinstatement fees. Michigan is a no-fault state — uninsured drivers face additional liability after a crash.",
    es: "Conducir sin seguro es un delito menor. Las sanciones pueden incluir multas, tiempo en prisión, suspensión de la licencia y tarifas de reinstalación. Míchigan es un estado de no-culpa — los conductores sin seguro enfrentan responsabilidad adicional después de un choque.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.michigan.gov/difs/consumers/insurance/auto/auto-insurance-overview",
    "MI DIFS — enforcement summary.",
  ),
};

const MN: PerStateInsurance = {
  stateCode: "MN",
  displayName: { en: "Minnesota", es: "Minnesota" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://www.commerce.mn.gov/consumers/insurance/auto-insurance/",
      "Minnesota Department of Commerce — Auto Insurance (no-fault state).",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 30_000,
    citation: cite(
      "https://www.commerce.mn.gov/consumers/insurance/auto-insurance/",
      "MN Commerce — minimum liability 30/60/10 plus PIP $40,000.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 60_000,
    citation: cite(
      "https://www.commerce.mn.gov/consumers/insurance/auto-insurance/",
      "MN Commerce — minimum liability 30/60/10.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 10_000,
    citation: cite(
      "https://www.commerce.mn.gov/consumers/insurance/auto-insurance/",
      "MN Commerce — minimum liability 30/60/10.",
    ),
  },
  uninsuredMotorist: {
    value: "required",
    citation: cite(
      "https://www.commerce.mn.gov/consumers/insurance/auto-insurance/",
      "MN — UM/UIM 25/50 compulsory.",
    ),
  },
  pipNoFault: {
    value: "required",
    citation: cite(
      "https://www.commerce.mn.gov/consumers/insurance/auto-insurance/",
      "MN — no-fault state; PIP $40,000 (split medical/wage) required.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance is a misdemeanor. Penalties can include fines, license and registration suspension, and reinstatement fees. SR-22 filings may be required.",
    es: "Conducir sin seguro es un delito menor. Las sanciones pueden incluir multas, suspensión de la licencia y la matrícula, y tarifas de reinstalación. Puede requerirse la presentación de un SR-22.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.commerce.mn.gov/consumers/insurance/auto-insurance/",
    "MN Commerce — enforcement summary.",
  ),
};

const MS: PerStateInsurance = {
  stateCode: "MS",
  displayName: { en: "Mississippi", es: "Misisipi" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://www.mid.ms.gov/consumers/auto-insurance.aspx",
      "Mississippi Insurance Department — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.mid.ms.gov/consumers/auto-insurance.aspx",
      "MS Insurance Dept — minimum liability 25/50/25.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://www.mid.ms.gov/consumers/auto-insurance.aspx",
      "MS Insurance Dept — minimum liability 25/50/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.mid.ms.gov/consumers/auto-insurance.aspx",
      "MS Insurance Dept — minimum liability 25/50/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://www.mid.ms.gov/consumers/auto-insurance.aspx",
      "MS Insurance Dept — UM/UIM offered; written rejection.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://www.mid.ms.gov/consumers/auto-insurance.aspx",
      "MS is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license suspension, and reinstatement fees. SR-22 filings may be required.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y tarifas de reinstalación. Puede requerirse la presentación de un SR-22.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.mid.ms.gov/consumers/auto-insurance.aspx",
    "MS Insurance Dept — enforcement summary.",
  ),
};

const MO: PerStateInsurance = {
  stateCode: "MO",
  displayName: { en: "Missouri", es: "Misuri" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://insurance.mo.gov/consumers/auto/",
      "Missouri Department of Commerce and Insurance — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://insurance.mo.gov/consumers/auto/",
      "MO DCI — minimum liability 25/50/25 (raised effective 2023-01-01).",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://insurance.mo.gov/consumers/auto/",
      "MO DCI — minimum liability 25/50/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://insurance.mo.gov/consumers/auto/",
      "MO DCI — minimum liability 25/50/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required",
    citation: cite(
      "https://insurance.mo.gov/consumers/auto/",
      "MO DCI — UM coverage 25/50 compulsory.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://insurance.mo.gov/consumers/auto/",
      "MO is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license suspension, and reinstatement fees. SR-22 filings are required for reinstatement.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y tarifas de reinstalación. Se requiere la presentación de un SR-22 para la reinstalación.",
  },
  uninsuredPenaltyCitation: cite(
    "https://insurance.mo.gov/consumers/auto/",
    "MO DCI — enforcement summary.",
  ),
};

const MT: PerStateInsurance = {
  stateCode: "MT",
  displayName: { en: "Montana", es: "Montana" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://csimt.gov/insurance/auto/",
      "Montana Commissioner of Securities and Insurance — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://csimt.gov/insurance/auto/",
      "MT CSI — minimum liability 25/50/20.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://csimt.gov/insurance/auto/",
      "MT CSI — minimum liability 25/50/20.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 20_000,
    citation: cite(
      "https://csimt.gov/insurance/auto/",
      "MT CSI — minimum liability 25/50/20.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://csimt.gov/insurance/auto/",
      "MT CSI — UM/UIM offered; rejection in writing.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://csimt.gov/insurance/auto/",
      "MT is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license suspension, and reinstatement fees. SR-22 filings may be required.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y tarifas de reinstalación. Puede requerirse la presentación de un SR-22.",
  },
  uninsuredPenaltyCitation: cite(
    "https://csimt.gov/insurance/auto/",
    "MT CSI — enforcement summary.",
  ),
};

const NE: PerStateInsurance = {
  stateCode: "NE",
  displayName: { en: "Nebraska", es: "Nebraska" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://doi.nebraska.gov/consumer/automobile-insurance",
      "Nebraska Department of Insurance — Automobile Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://doi.nebraska.gov/consumer/automobile-insurance",
      "NE DOI — minimum liability 25/50/25.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://doi.nebraska.gov/consumer/automobile-insurance",
      "NE DOI — minimum liability 25/50/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://doi.nebraska.gov/consumer/automobile-insurance",
      "NE DOI — minimum liability 25/50/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required",
    citation: cite(
      "https://doi.nebraska.gov/consumer/automobile-insurance",
      "NE DOI — UM 25/50 compulsory.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://doi.nebraska.gov/consumer/automobile-insurance",
      "NE is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license suspension, and reinstatement fees. SR-22 filings are required for reinstatement.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y tarifas de reinstalación. Se requiere la presentación de un SR-22 para la reinstalación.",
  },
  uninsuredPenaltyCitation: cite(
    "https://doi.nebraska.gov/consumer/automobile-insurance",
    "NE DOI — enforcement summary.",
  ),
};

const NV: PerStateInsurance = {
  stateCode: "NV",
  displayName: { en: "Nevada", es: "Nevada" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://doi.nv.gov/Consumers/Automobile_Insurance/",
      "Nevada Division of Insurance — Automobile Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://doi.nv.gov/Consumers/Automobile_Insurance/",
      "NV Division of Insurance — minimum liability 25/50/20 (effective 2018-07-01).",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://doi.nv.gov/Consumers/Automobile_Insurance/",
      "NV Division of Insurance — minimum liability 25/50/20.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 20_000,
    citation: cite(
      "https://doi.nv.gov/Consumers/Automobile_Insurance/",
      "NV Division of Insurance — minimum liability 25/50/20.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://doi.nv.gov/Consumers/Automobile_Insurance/",
      "NV Division of Insurance — UM/UIM offered; written rejection.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://doi.nv.gov/Consumers/Automobile_Insurance/",
      "NV is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license and registration suspension, reinstatement fees, and SR-22 filings. Nevada DMV verifies coverage continuously through the LIVE system.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y la matrícula, tarifas de reinstalación y presentación de un SR-22. El DMV de Nevada verifica la cobertura de forma continua mediante el sistema LIVE.",
  },
  uninsuredPenaltyCitation: cite(
    "https://doi.nv.gov/Consumers/Automobile_Insurance/",
    "NV Division of Insurance — enforcement summary including LIVE verification system.",
  ),
};

const NH: PerStateInsurance = {
  stateCode: "NH",
  displayName: { en: "New Hampshire", es: "Nuevo Hampshire" },
  mandatoryAtRegistration: {
    // NH special case: only state where private liability insurance is
    // not mandatory at registration. Drivers may post a bond / file a
    // certificate of financial responsibility instead.
    value: "required_with_alternatives",
    citation: cite(
      "https://www.nh.gov/insurance/consumers/auto.htm",
      "New Hampshire Insurance Department — Auto Insurance. NH does not require liability insurance for most drivers; a posted bond or certificate of financial responsibility (e.g., SR-22) may substitute.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.nh.gov/insurance/consumers/auto.htm",
      "NH DOI — when insurance IS purchased, minimum liability 25/50/25 plus Med-Pay $1,000.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://www.nh.gov/insurance/consumers/auto.htm",
      "NH DOI — minimum liability 25/50/25 when policy is in effect.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.nh.gov/insurance/consumers/auto.htm",
      "NH DOI — minimum liability 25/50/25 when policy is in effect.",
    ),
  },
  uninsuredMotorist: {
    value: "required",
    citation: cite(
      "https://www.nh.gov/insurance/consumers/auto.htm",
      "NH DOI — when insurance IS purchased, UM/UIM at policy BIL limits is required.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://www.nh.gov/insurance/consumers/auto.htm",
      "NH is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "New Hampshire does not require all drivers to carry insurance, but every driver remains personally liable for damages they cause. Drivers convicted of certain offenses (DWI, at-fault crashes, etc.) must file a certificate of financial responsibility (SR-22 or bond) with the DMV. Failure to satisfy a judgment leads to license and registration suspension.",
    es: "Nuevo Hampshire no exige a todos los conductores tener seguro, pero cada conductor sigue siendo personalmente responsable por los daños que cause. Los conductores condenados por ciertos delitos (DWI, choques con culpa, etc.) deben presentar un certificado de responsabilidad financiera (SR-22 o fianza) ante el DMV. El incumplimiento de una sentencia conlleva la suspensión de la licencia y la matrícula.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.nh.gov/insurance/consumers/auto.htm",
    "NH DOI — Auto Insurance overview including SR-22 / bond alternative.",
  ),
};

const NJ: PerStateInsurance = {
  stateCode: "NJ",
  displayName: { en: "New Jersey", es: "Nueva Jersey" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://www.state.nj.us/dobi/division_consumers/insurance/autobuyer.htm",
      "New Jersey Department of Banking and Insurance — Auto Insurance Buyer's Guide (no-fault state).",
    ),
  },
  minBodilyInjuryPerPerson: {
    // Standard policy default; Basic Policy minimum is even lower.
    amountUsd: 25_000,
    citation: cite(
      "https://www.state.nj.us/dobi/division_consumers/insurance/autobuyer.htm",
      "NJ DOBI — Standard policy minimum 25/50/25 (Basic policy minimum 0/0/5 with PIP $15k).",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://www.state.nj.us/dobi/division_consumers/insurance/autobuyer.htm",
      "NJ DOBI — Standard policy minimum 25/50/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.state.nj.us/dobi/division_consumers/insurance/autobuyer.htm",
      "NJ DOBI — Standard policy minimum 25/50/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required",
    citation: cite(
      "https://www.state.nj.us/dobi/division_consumers/insurance/autobuyer.htm",
      "NJ DOBI — UM/UIM 25/50/25 compulsory on Standard policies.",
    ),
  },
  pipNoFault: {
    value: "required",
    citation: cite(
      "https://www.state.nj.us/dobi/division_consumers/insurance/autobuyer.htm",
      "NJ — no-fault state; PIP $15,000 minimum required (up to $250,000 for severe injuries).",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance is a serious offense. First-offense penalties include license suspension up to one year, fines, community service, and reinstatement fees. New Jersey is a no-fault state — uninsured drivers face additional liability after a crash.",
    es: "Conducir sin seguro es una infracción grave. Las sanciones por la primera infracción incluyen suspensión de la licencia de hasta un año, multas, servicio comunitario y tarifas de reinstalación. Nueva Jersey es un estado de no-culpa — los conductores sin seguro enfrentan responsabilidad adicional después de un choque.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.state.nj.us/dobi/division_consumers/insurance/autobuyer.htm",
    "NJ DOBI — enforcement summary.",
  ),
};

const NM: PerStateInsurance = {
  stateCode: "NM",
  displayName: { en: "New Mexico", es: "Nuevo México" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://www.osi.state.nm.us/Consumer/Auto.aspx",
      "New Mexico Office of Superintendent of Insurance — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.osi.state.nm.us/Consumer/Auto.aspx",
      "NM OSI — minimum liability 25/50/10.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://www.osi.state.nm.us/Consumer/Auto.aspx",
      "NM OSI — minimum liability 25/50/10.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 10_000,
    citation: cite(
      "https://www.osi.state.nm.us/Consumer/Auto.aspx",
      "NM OSI — minimum liability 25/50/10.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://www.osi.state.nm.us/Consumer/Auto.aspx",
      "NM OSI — UM/UIM offered; written rejection required.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://www.osi.state.nm.us/Consumer/Auto.aspx",
      "NM is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license suspension, and reinstatement fees. SR-22 filings may be required.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y tarifas de reinstalación. Puede requerirse la presentación de un SR-22.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.osi.state.nm.us/Consumer/Auto.aspx",
    "NM OSI — enforcement summary.",
  ),
};

const NY: PerStateInsurance = {
  stateCode: "NY",
  displayName: { en: "New York", es: "Nueva York" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://www.dfs.ny.gov/consumers/auto_insurance",
      "New York Department of Financial Services — Auto Insurance (no-fault state).",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.dfs.ny.gov/consumers/auto_insurance",
      "NY DFS — minimum liability 25/50/10 plus PIP $50,000 and UM/SUM 25/50.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://www.dfs.ny.gov/consumers/auto_insurance",
      "NY DFS — minimum BIL 25/50.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 10_000,
    citation: cite(
      "https://www.dfs.ny.gov/consumers/auto_insurance",
      "NY DFS — minimum PD $10,000.",
    ),
  },
  uninsuredMotorist: {
    value: "required",
    citation: cite(
      "https://www.dfs.ny.gov/consumers/auto_insurance",
      "NY DFS — UM 25/50 compulsory; Supplemental Underinsured (SUM) offered.",
    ),
  },
  pipNoFault: {
    value: "required",
    citation: cite(
      "https://www.dfs.ny.gov/consumers/auto_insurance",
      "NY — no-fault state; PIP $50,000 required.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in license and registration suspension, fines, civil penalties up to several hundred dollars, and an additional fee for each day the registration was uninsured. New York is a no-fault state — uninsured drivers face additional liability after a crash.",
    es: "Conducir sin seguro puede resultar en la suspensión de la licencia y la matrícula, multas, sanciones civiles de hasta varios cientos de dólares, y una tarifa adicional por cada día que la matrícula estuvo sin seguro. Nueva York es un estado de no-culpa — los conductores sin seguro enfrentan responsabilidad adicional después de un choque.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.dfs.ny.gov/consumers/auto_insurance",
    "NY DFS — enforcement summary.",
  ),
};

const NC: PerStateInsurance = {
  stateCode: "NC",
  displayName: { en: "North Carolina", es: "Carolina del Norte" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://www.ncdoi.gov/consumers/automobile-insurance",
      "North Carolina Department of Insurance — Automobile Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 50_000,
    citation: cite(
      "https://www.ncdoi.gov/consumers/automobile-insurance",
      "NC DOI — minimum liability 50/100/50 (effective 2025-07-01).",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 100_000,
    citation: cite(
      "https://www.ncdoi.gov/consumers/automobile-insurance",
      "NC DOI — minimum liability 50/100/50.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 50_000,
    citation: cite(
      "https://www.ncdoi.gov/consumers/automobile-insurance",
      "NC DOI — minimum liability 50/100/50.",
    ),
  },
  uninsuredMotorist: {
    value: "required",
    citation: cite(
      "https://www.ncdoi.gov/consumers/automobile-insurance",
      "NC DOI — UM/UIM coverage required at minimum BIL/PD limits.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://www.ncdoi.gov/consumers/automobile-insurance",
      "NC is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license-plate revocation, license suspension, and reinstatement fees. SR-22 filings may be required.",
    es: "Conducir sin seguro puede resultar en multas, revocación de la placa, suspensión de la licencia y tarifas de reinstalación. Puede requerirse la presentación de un SR-22.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.ncdoi.gov/consumers/automobile-insurance",
    "NC DOI — enforcement summary.",
  ),
};

const ND: PerStateInsurance = {
  stateCode: "ND",
  displayName: { en: "North Dakota", es: "Dakota del Norte" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://www.insurance.nd.gov/consumers/auto",
      "North Dakota Insurance Department — Auto Insurance (no-fault state).",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.insurance.nd.gov/consumers/auto",
      "ND DOI — minimum liability 25/50/25 plus PIP $30,000.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://www.insurance.nd.gov/consumers/auto",
      "ND DOI — minimum liability 25/50/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.insurance.nd.gov/consumers/auto",
      "ND DOI — minimum liability 25/50/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required",
    citation: cite(
      "https://www.insurance.nd.gov/consumers/auto",
      "ND DOI — UM 25/50 and UIM 25/50 compulsory.",
    ),
  },
  pipNoFault: {
    value: "required",
    citation: cite(
      "https://www.insurance.nd.gov/consumers/auto",
      "ND — no-fault state; PIP $30,000 required.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license suspension, and reinstatement fees. North Dakota is a no-fault state — uninsured drivers face additional liability after a crash.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y tarifas de reinstalación. Dakota del Norte es un estado de no-culpa — los conductores sin seguro enfrentan responsabilidad adicional después de un choque.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.insurance.nd.gov/consumers/auto",
    "ND DOI — enforcement summary.",
  ),
};

const OH: PerStateInsurance = {
  stateCode: "OH",
  displayName: { en: "Ohio", es: "Ohio" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://insurance.ohio.gov/wps/portal/gov/odi/consumers/automobile",
      "Ohio Department of Insurance — Automobile Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://insurance.ohio.gov/wps/portal/gov/odi/consumers/automobile",
      "OH DOI — minimum liability 25/50/25.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://insurance.ohio.gov/wps/portal/gov/odi/consumers/automobile",
      "OH DOI — minimum liability 25/50/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://insurance.ohio.gov/wps/portal/gov/odi/consumers/automobile",
      "OH DOI — minimum liability 25/50/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://insurance.ohio.gov/wps/portal/gov/odi/consumers/automobile",
      "OH DOI — UM/UIM offered; written rejection.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://insurance.ohio.gov/wps/portal/gov/odi/consumers/automobile",
      "OH is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license and registration suspension, and reinstatement fees. SR-22 filings are required for reinstatement.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y la matrícula, y tarifas de reinstalación. Se requiere la presentación de un SR-22 para la reinstalación.",
  },
  uninsuredPenaltyCitation: cite(
    "https://insurance.ohio.gov/wps/portal/gov/odi/consumers/automobile",
    "OH DOI — enforcement summary.",
  ),
};

const OK: PerStateInsurance = {
  stateCode: "OK",
  displayName: { en: "Oklahoma", es: "Oklahoma" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://www.oid.ok.gov/consumers/insurance-basics/auto-insurance/",
      "Oklahoma Insurance Department — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.oid.ok.gov/consumers/insurance-basics/auto-insurance/",
      "OK OID — minimum liability 25/50/25.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://www.oid.ok.gov/consumers/insurance-basics/auto-insurance/",
      "OK OID — minimum liability 25/50/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.oid.ok.gov/consumers/insurance-basics/auto-insurance/",
      "OK OID — minimum liability 25/50/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://www.oid.ok.gov/consumers/insurance-basics/auto-insurance/",
      "OK OID — UM/UIM offered; written rejection.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://www.oid.ok.gov/consumers/insurance-basics/auto-insurance/",
      "OK is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license suspension, vehicle impoundment, and reinstatement fees. SR-22 filings may be required.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia, incautación del vehículo y tarifas de reinstalación. Puede requerirse la presentación de un SR-22.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.oid.ok.gov/consumers/insurance-basics/auto-insurance/",
    "OK OID — enforcement summary.",
  ),
};

const OR: PerStateInsurance = {
  stateCode: "OR",
  displayName: { en: "Oregon", es: "Oregón" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://dfr.oregon.gov/insure/auto/Pages/index.aspx",
      "Oregon Division of Financial Regulation — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://dfr.oregon.gov/insure/auto/Pages/index.aspx",
      "OR DFR — minimum liability 25/50/20 plus PIP $15,000 and UM 25/50.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://dfr.oregon.gov/insure/auto/Pages/index.aspx",
      "OR DFR — minimum BIL 25/50.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 20_000,
    citation: cite(
      "https://dfr.oregon.gov/insure/auto/Pages/index.aspx",
      "OR DFR — minimum PD $20,000.",
    ),
  },
  uninsuredMotorist: {
    value: "required",
    citation: cite(
      "https://dfr.oregon.gov/insure/auto/Pages/index.aspx",
      "OR DFR — UM 25/50 compulsory.",
    ),
  },
  pipNoFault: {
    value: "optional",
    citation: cite(
      "https://dfr.oregon.gov/insure/auto/Pages/index.aspx",
      "OR — PIP $15,000 (Personal Injury Protection) compulsory but Oregon is not a no-fault state — PIP is mandatory add-on.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license suspension, vehicle impoundment, and reinstatement fees. SR-22 filings are required for reinstatement.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia, incautación del vehículo y tarifas de reinstalación. Se requiere la presentación de un SR-22 para la reinstalación.",
  },
  uninsuredPenaltyCitation: cite(
    "https://dfr.oregon.gov/insure/auto/Pages/index.aspx",
    "OR DFR — enforcement summary.",
  ),
};

const PA: PerStateInsurance = {
  stateCode: "PA",
  displayName: { en: "Pennsylvania", es: "Pensilvania" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://www.insurance.pa.gov/Coverage/Pages/AutoCoverage.aspx",
      "Pennsylvania Insurance Department — Auto Coverage (choice no-fault).",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 15_000,
    citation: cite(
      "https://www.insurance.pa.gov/Coverage/Pages/AutoCoverage.aspx",
      "PA DOI — minimum liability 15/30/5 plus First-Party Benefits (PIP-equivalent) $5,000 medical.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 30_000,
    citation: cite(
      "https://www.insurance.pa.gov/Coverage/Pages/AutoCoverage.aspx",
      "PA DOI — minimum BIL 15/30.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 5_000,
    citation: cite(
      "https://www.insurance.pa.gov/Coverage/Pages/AutoCoverage.aspx",
      "PA DOI — minimum PD $5,000.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://www.insurance.pa.gov/Coverage/Pages/AutoCoverage.aspx",
      "PA DOI — UM/UIM offered; written rejection.",
    ),
  },
  pipNoFault: {
    value: "required",
    citation: cite(
      "https://www.insurance.pa.gov/Coverage/Pages/AutoCoverage.aspx",
      "PA — choice no-fault state; First-Party Medical Benefits (PIP-equivalent) $5,000 minimum required.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in license and registration suspension for three months, fines, and reinstatement fees. SR-22 filings may be required. Pennsylvania is a choice-no-fault state — special PIP rules apply.",
    es: "Conducir sin seguro puede resultar en la suspensión de la licencia y la matrícula durante tres meses, multas y tarifas de reinstalación. Puede requerirse la presentación de un SR-22. Pensilvania es un estado de no-culpa de elección — se aplican reglas especiales de PIP.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.insurance.pa.gov/Coverage/Pages/AutoCoverage.aspx",
    "PA DOI — enforcement summary.",
  ),
};

const RI: PerStateInsurance = {
  stateCode: "RI",
  displayName: { en: "Rhode Island", es: "Rhode Island" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://dbr.ri.gov/divisions/insurance/auto-insurance",
      "Rhode Island Department of Business Regulation, Insurance Division — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://dbr.ri.gov/divisions/insurance/auto-insurance",
      "RI DBR — minimum liability 25/50/25.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://dbr.ri.gov/divisions/insurance/auto-insurance",
      "RI DBR — minimum liability 25/50/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://dbr.ri.gov/divisions/insurance/auto-insurance",
      "RI DBR — minimum liability 25/50/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://dbr.ri.gov/divisions/insurance/auto-insurance",
      "RI DBR — UM/UIM offered; written rejection.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://dbr.ri.gov/divisions/insurance/auto-insurance",
      "RI is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license suspension, and reinstatement fees. SR-22 filings may be required.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y tarifas de reinstalación. Puede requerirse la presentación de un SR-22.",
  },
  uninsuredPenaltyCitation: cite(
    "https://dbr.ri.gov/divisions/insurance/auto-insurance",
    "RI DBR — enforcement summary.",
  ),
};

const SC: PerStateInsurance = {
  stateCode: "SC",
  displayName: { en: "South Carolina", es: "Carolina del Sur" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://doi.sc.gov/210/Auto",
      "South Carolina Department of Insurance — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://doi.sc.gov/210/Auto",
      "SC DOI — minimum liability 25/50/25.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://doi.sc.gov/210/Auto",
      "SC DOI — minimum liability 25/50/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://doi.sc.gov/210/Auto",
      "SC DOI — minimum liability 25/50/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required",
    citation: cite(
      "https://doi.sc.gov/210/Auto",
      "SC DOI — UM 25/50/25 compulsory.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://doi.sc.gov/210/Auto",
      "SC is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license and registration suspension, vehicle impoundment, and an Uninsured Motorist Fee. SR-22 filings may be required.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y la matrícula, incautación del vehículo y una tarifa de automovilista no asegurado. Puede requerirse la presentación de un SR-22.",
  },
  uninsuredPenaltyCitation: cite(
    "https://doi.sc.gov/210/Auto",
    "SC DOI — enforcement summary.",
  ),
};

const SD: PerStateInsurance = {
  stateCode: "SD",
  displayName: { en: "South Dakota", es: "Dakota del Sur" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://dlr.sd.gov/insurance/auto.aspx",
      "South Dakota Division of Insurance — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://dlr.sd.gov/insurance/auto.aspx",
      "SD Insurance — minimum liability 25/50/25.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://dlr.sd.gov/insurance/auto.aspx",
      "SD Insurance — minimum liability 25/50/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://dlr.sd.gov/insurance/auto.aspx",
      "SD Insurance — minimum liability 25/50/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required",
    citation: cite(
      "https://dlr.sd.gov/insurance/auto.aspx",
      "SD Insurance — UM 25/50 compulsory.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://dlr.sd.gov/insurance/auto.aspx",
      "SD is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license suspension, and reinstatement fees. SR-22 filings may be required.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y tarifas de reinstalación. Puede requerirse la presentación de un SR-22.",
  },
  uninsuredPenaltyCitation: cite(
    "https://dlr.sd.gov/insurance/auto.aspx",
    "SD Insurance — enforcement summary.",
  ),
};

const TN: PerStateInsurance = {
  stateCode: "TN",
  displayName: { en: "Tennessee", es: "Tennessee" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://www.tn.gov/commerce/insurance/consumer-resources/auto-insurance.html",
      "Tennessee Department of Commerce and Insurance — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.tn.gov/commerce/insurance/consumer-resources/auto-insurance.html",
      "TN DCI — minimum liability 25/50/25.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://www.tn.gov/commerce/insurance/consumer-resources/auto-insurance.html",
      "TN DCI — minimum liability 25/50/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.tn.gov/commerce/insurance/consumer-resources/auto-insurance.html",
      "TN DCI — minimum liability 25/50/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://www.tn.gov/commerce/insurance/consumer-resources/auto-insurance.html",
      "TN DCI — UM/UIM offered; written rejection.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://www.tn.gov/commerce/insurance/consumer-resources/auto-insurance.html",
      "TN is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license suspension, and reinstatement fees. SR-22 filings may be required.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y tarifas de reinstalación. Puede requerirse la presentación de un SR-22.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.tn.gov/commerce/insurance/consumer-resources/auto-insurance.html",
    "TN DCI — enforcement summary.",
  ),
};

const TX: PerStateInsurance = {
  stateCode: "TX",
  displayName: { en: "Texas", es: "Texas" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://www.tdi.texas.gov/pubs/consumer/cb020.html",
      "Texas Department of Insurance — Automobile Insurance Made Easy.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 30_000,
    citation: cite(
      "https://www.tdi.texas.gov/pubs/consumer/cb020.html",
      "TX TDI — minimum liability 30/60/25.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 60_000,
    citation: cite(
      "https://www.tdi.texas.gov/pubs/consumer/cb020.html",
      "TX TDI — minimum liability 30/60/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.tdi.texas.gov/pubs/consumer/cb020.html",
      "TX TDI — minimum liability 30/60/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://www.tdi.texas.gov/pubs/consumer/cb020.html",
      "TX TDI — UM/UIM offered; written rejection.",
    ),
  },
  pipNoFault: {
    value: "optional",
    citation: cite(
      "https://www.tdi.texas.gov/pubs/consumer/cb020.html",
      "TX TDI — PIP $2,500 must be offered (waivable in writing).",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines from $175 to $1,000, license and registration suspension, and reinstatement fees. SR-22 filings are required for reinstatement.",
    es: "Conducir sin seguro puede resultar en multas de $175 a $1,000, suspensión de la licencia y la matrícula, y tarifas de reinstalación. Se requiere la presentación de un SR-22 para la reinstalación.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.tdi.texas.gov/pubs/consumer/cb020.html",
    "TX TDI — enforcement summary including fines schedule.",
  ),
};

const UT: PerStateInsurance = {
  stateCode: "UT",
  displayName: { en: "Utah", es: "Utah" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://insurance.utah.gov/consumers/auto/",
      "Utah Insurance Department — Auto Insurance (no-fault state).",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://insurance.utah.gov/consumers/auto/",
      "UT Insurance — minimum liability 25/65/15 plus PIP $3,000.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 65_000,
    citation: cite(
      "https://insurance.utah.gov/consumers/auto/",
      "UT Insurance — minimum BIL 25/65.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 15_000,
    citation: cite(
      "https://insurance.utah.gov/consumers/auto/",
      "UT Insurance — minimum PD $15,000.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://insurance.utah.gov/consumers/auto/",
      "UT Insurance — UM/UIM offered; written rejection.",
    ),
  },
  pipNoFault: {
    value: "required",
    citation: cite(
      "https://insurance.utah.gov/consumers/auto/",
      "UT — no-fault state; PIP $3,000 minimum required.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license and registration suspension, and reinstatement fees. SR-22 filings may be required. Utah is a no-fault state — uninsured drivers face additional liability after a crash.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y la matrícula, y tarifas de reinstalación. Puede requerirse la presentación de un SR-22. Utah es un estado de no-culpa — los conductores sin seguro enfrentan responsabilidad adicional después de un choque.",
  },
  uninsuredPenaltyCitation: cite(
    "https://insurance.utah.gov/consumers/auto/",
    "UT Insurance — enforcement summary.",
  ),
};

const VT: PerStateInsurance = {
  stateCode: "VT",
  displayName: { en: "Vermont", es: "Vermont" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://dfr.vermont.gov/consumers/auto-insurance",
      "Vermont Department of Financial Regulation — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://dfr.vermont.gov/consumers/auto-insurance",
      "VT DFR — minimum liability 25/50/10.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://dfr.vermont.gov/consumers/auto-insurance",
      "VT DFR — minimum liability 25/50/10.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 10_000,
    citation: cite(
      "https://dfr.vermont.gov/consumers/auto-insurance",
      "VT DFR — minimum liability 25/50/10.",
    ),
  },
  uninsuredMotorist: {
    value: "required",
    citation: cite(
      "https://dfr.vermont.gov/consumers/auto-insurance",
      "VT DFR — UM/UIM 50/100/10 compulsory.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://dfr.vermont.gov/consumers/auto-insurance",
      "VT is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license suspension, and reinstatement fees. SR-22 filings may be required.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y tarifas de reinstalación. Puede requerirse la presentación de un SR-22.",
  },
  uninsuredPenaltyCitation: cite(
    "https://dfr.vermont.gov/consumers/auto-insurance",
    "VT DFR — enforcement summary.",
  ),
};

const VA: PerStateInsurance = {
  stateCode: "VA",
  displayName: { en: "Virginia", es: "Virginia" },
  mandatoryAtRegistration: {
    // VA had an "uninsured-motor-vehicle fee" pre-2024 that was a true
    // alternative; effective 2024-07-01 the alternative was removed —
    // every driver now needs liability insurance. Marking as
    // required_all_drivers per the current rule.
    value: "required_all_drivers",
    citation: cite(
      "https://scc.virginia.gov/pages/Automobile-Insurance",
      "Virginia State Corporation Commission, Bureau of Insurance — Automobile Insurance. As of 2024-07-01 the uninsured-motor-vehicle fee alternative was eliminated; liability insurance is now mandatory for all drivers.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 50_000,
    citation: cite(
      "https://scc.virginia.gov/pages/Automobile-Insurance",
      "VA SCC — minimum liability 50/100/25 effective 2025-01-01.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 100_000,
    citation: cite(
      "https://scc.virginia.gov/pages/Automobile-Insurance",
      "VA SCC — minimum liability 50/100/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://scc.virginia.gov/pages/Automobile-Insurance",
      "VA SCC — minimum liability 50/100/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required",
    citation: cite(
      "https://scc.virginia.gov/pages/Automobile-Insurance",
      "VA SCC — UM/UIM at minimum BIL/PD compulsory.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://scc.virginia.gov/pages/Automobile-Insurance",
      "VA is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license and registration suspension for at least three years, and reinstatement fees. SR-22 filings are required for reinstatement.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y la matrícula durante al menos tres años, y tarifas de reinstalación. Se requiere la presentación de un SR-22 para la reinstalación.",
  },
  uninsuredPenaltyCitation: cite(
    "https://scc.virginia.gov/pages/Automobile-Insurance",
    "VA SCC — enforcement summary.",
  ),
};

const WA: PerStateInsurance = {
  stateCode: "WA",
  displayName: { en: "Washington", es: "Washington" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://www.insurance.wa.gov/auto-insurance",
      "Washington State Office of the Insurance Commissioner — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.insurance.wa.gov/auto-insurance",
      "WA OIC — minimum liability 25/50/10.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://www.insurance.wa.gov/auto-insurance",
      "WA OIC — minimum liability 25/50/10.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 10_000,
    citation: cite(
      "https://www.insurance.wa.gov/auto-insurance",
      "WA OIC — minimum liability 25/50/10.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://www.insurance.wa.gov/auto-insurance",
      "WA OIC — UM/UIM offered; written rejection.",
    ),
  },
  pipNoFault: {
    value: "optional",
    citation: cite(
      "https://www.insurance.wa.gov/auto-insurance",
      "WA OIC — PIP must be offered (waivable in writing).",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in traffic-infraction fines starting around $550, plus license suspension on repeat offenses. SR-22 filings may be required.",
    es: "Conducir sin seguro puede resultar en multas por infracción de tráfico que comienzan alrededor de $550, además de la suspensión de la licencia por infracciones repetidas. Puede requerirse la presentación de un SR-22.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.insurance.wa.gov/auto-insurance",
    "WA OIC — enforcement summary.",
  ),
};

const WV: PerStateInsurance = {
  stateCode: "WV",
  displayName: { en: "West Virginia", es: "Virginia Occidental" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://www.wvinsurance.gov/Consumer-Services/Auto-Insurance",
      "West Virginia Offices of the Insurance Commissioner — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.wvinsurance.gov/Consumer-Services/Auto-Insurance",
      "WV OIC — minimum liability 25/50/25.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://www.wvinsurance.gov/Consumer-Services/Auto-Insurance",
      "WV OIC — minimum liability 25/50/25.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 25_000,
    citation: cite(
      "https://www.wvinsurance.gov/Consumer-Services/Auto-Insurance",
      "WV OIC — minimum liability 25/50/25.",
    ),
  },
  uninsuredMotorist: {
    value: "required",
    citation: cite(
      "https://www.wvinsurance.gov/Consumer-Services/Auto-Insurance",
      "WV OIC — UM 25/50/25 compulsory.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://www.wvinsurance.gov/Consumer-Services/Auto-Insurance",
      "WV is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license and registration suspension, and reinstatement fees. SR-22 filings are required for reinstatement.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y la matrícula, y tarifas de reinstalación. Se requiere la presentación de un SR-22 para la reinstalación.",
  },
  uninsuredPenaltyCitation: cite(
    "https://www.wvinsurance.gov/Consumer-Services/Auto-Insurance",
    "WV OIC — enforcement summary.",
  ),
};

const WI: PerStateInsurance = {
  stateCode: "WI",
  displayName: { en: "Wisconsin", es: "Wisconsin" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://oci.wi.gov/Pages/Consumers/Auto.aspx",
      "Wisconsin Office of the Commissioner of Insurance — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://oci.wi.gov/Pages/Consumers/Auto.aspx",
      "WI OCI — minimum liability 25/50/10.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://oci.wi.gov/Pages/Consumers/Auto.aspx",
      "WI OCI — minimum liability 25/50/10.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 10_000,
    citation: cite(
      "https://oci.wi.gov/Pages/Consumers/Auto.aspx",
      "WI OCI — minimum liability 25/50/10.",
    ),
  },
  uninsuredMotorist: {
    value: "required",
    citation: cite(
      "https://oci.wi.gov/Pages/Consumers/Auto.aspx",
      "WI OCI — UM 25/50 compulsory.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://oci.wi.gov/Pages/Consumers/Auto.aspx",
      "WI is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines up to $500, license suspension, and reinstatement fees. SR-22 filings may be required.",
    es: "Conducir sin seguro puede resultar en multas de hasta $500, suspensión de la licencia y tarifas de reinstalación. Puede requerirse la presentación de un SR-22.",
  },
  uninsuredPenaltyCitation: cite(
    "https://oci.wi.gov/Pages/Consumers/Auto.aspx",
    "WI OCI — enforcement summary.",
  ),
};

const WY: PerStateInsurance = {
  stateCode: "WY",
  displayName: { en: "Wyoming", es: "Wyoming" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://doi.wyo.gov/consumers/auto-insurance",
      "Wyoming Department of Insurance — Auto Insurance.",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://doi.wyo.gov/consumers/auto-insurance",
      "WY DOI — minimum liability 25/50/20.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://doi.wyo.gov/consumers/auto-insurance",
      "WY DOI — minimum liability 25/50/20.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 20_000,
    citation: cite(
      "https://doi.wyo.gov/consumers/auto-insurance",
      "WY DOI — minimum liability 25/50/20.",
    ),
  },
  uninsuredMotorist: {
    value: "required_offer_unless_rejected_in_writing",
    citation: cite(
      "https://doi.wyo.gov/consumers/auto-insurance",
      "WY DOI — UM/UIM offered; written rejection.",
    ),
  },
  pipNoFault: {
    value: "not_applicable",
    citation: cite(
      "https://doi.wyo.gov/consumers/auto-insurance",
      "WY is a tort state; PIP not applicable.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance can result in fines, license suspension, and reinstatement fees. SR-22 filings may be required.",
    es: "Conducir sin seguro puede resultar en multas, suspensión de la licencia y tarifas de reinstalación. Puede requerirse la presentación de un SR-22.",
  },
  uninsuredPenaltyCitation: cite(
    "https://doi.wyo.gov/consumers/auto-insurance",
    "WY DOI — enforcement summary.",
  ),
};

const DC: PerStateInsurance = {
  stateCode: "DC",
  displayName: { en: "District of Columbia", es: "Distrito de Columbia" },
  mandatoryAtRegistration: {
    value: "required_all_drivers",
    citation: cite(
      "https://disb.dc.gov/page/auto-insurance-information-disb",
      "DC Department of Insurance, Securities and Banking — Auto Insurance Information (no-fault add-on).",
    ),
  },
  minBodilyInjuryPerPerson: {
    amountUsd: 25_000,
    citation: cite(
      "https://disb.dc.gov/page/auto-insurance-information-disb",
      "DC DISB — minimum liability 25/50/10 plus PIP optional and UM 25/50/10.",
    ),
  },
  minBodilyInjuryPerAccident: {
    amountUsd: 50_000,
    citation: cite(
      "https://disb.dc.gov/page/auto-insurance-information-disb",
      "DC DISB — minimum BIL 25/50.",
    ),
  },
  minPropertyDamage: {
    amountUsd: 10_000,
    citation: cite(
      "https://disb.dc.gov/page/auto-insurance-information-disb",
      "DC DISB — minimum PD $10,000.",
    ),
  },
  uninsuredMotorist: {
    value: "required",
    citation: cite(
      "https://disb.dc.gov/page/auto-insurance-information-disb",
      "DC DISB — UM coverage required at minimum BIL/PD limits.",
    ),
  },
  pipNoFault: {
    // DC is a "choice no-fault add-on" jurisdiction — PIP is required
    // by statute as a base coverage but DC is technically a tort state
    // for liability purposes. ROADS-427 prompt explicitly groups DC
    // with the no-fault list, so we mark `required` per spec.
    value: "required",
    citation: cite(
      "https://disb.dc.gov/page/auto-insurance-information-disb",
      "DC DISB — PIP required as base coverage; DC operates a modified add-on no-fault system.",
    ),
  },
  uninsuredPenaltyText: {
    en: "Driving without insurance in the District can result in fines starting at $150 for the first 30 days and rising for longer lapses, license and registration suspension, and reinstatement fees. SR-22 filings may be required.",
    es: "Conducir sin seguro en el Distrito puede resultar en multas que comienzan en $150 por los primeros 30 días y aumentan para lapsos más largos, suspensión de la licencia y la matrícula, y tarifas de reinstalación. Puede requerirse la presentación de un SR-22.",
  },
  uninsuredPenaltyCitation: cite(
    "https://disb.dc.gov/page/auto-insurance-information-disb",
    "DC DISB — enforcement summary.",
  ),
};

// ── Final map ──────────────────────────────────────────────────────────────

export const PER_STATE_INSURANCE: Readonly<Record<string, PerStateInsurance>> = {
  AL,
  AK,
  AZ,
  AR,
  CA,
  CO,
  CT,
  DE,
  FL,
  GA,
  HI,
  ID,
  IL,
  IN,
  IA,
  KS,
  KY,
  LA,
  ME,
  MD,
  MA,
  MI,
  MN,
  MS,
  MO,
  MT,
  NE,
  NV,
  NH,
  NJ,
  NM,
  NY,
  NC,
  ND,
  OH,
  OK,
  OR,
  PA,
  RI,
  SC,
  SD,
  TN,
  TX,
  UT,
  VT,
  VA,
  WA,
  WV,
  WI,
  WY,
  DC,
};

/**
 * Look up an insurance entry by state code (case-insensitive).
 * Returns `undefined` for unknown codes — territories and any non-US
 * code surface as a clean miss instead of a fake row.
 */
export function getPerStateInsurance(code: string): PerStateInsurance | undefined {
  return PER_STATE_INSURANCE[code.toUpperCase()];
}

/**
 * Display order across the insurance card section. Matches the
 * "registration → BIL → PD → UM → PIP → penalty" reading flow that
 * mirrors how state DOI pages organize the information.
 */
export type InsuranceAttrName =
  | "mandatoryAtRegistration"
  | "minBodilyInjuryPerPerson"
  | "minBodilyInjuryPerAccident"
  | "minPropertyDamage"
  | "uninsuredMotorist"
  | "pipNoFault";

export const INSURANCE_ATTR_DISPLAY_ORDER: readonly InsuranceAttrName[] = [
  "mandatoryAtRegistration",
  "minBodilyInjuryPerPerson",
  "minBodilyInjuryPerAccident",
  "minPropertyDamage",
  "uninsuredMotorist",
  "pipNoFault",
] as const;
